import { DispatchType } from '../../../@types/redux-types'
import { priceMaximizerReset, priceMaximizerSuccess } from '../../reducers/priceMaximizer/priceMaximizerFeature'
import {
  postSimilarObjectsRentCount,
  postSimilarObjectsSellCount,
  postSimilarSearchRequestsRentCount,
  postSimilarSearchRequestsSellCount,
} from '../../../api/myPropertyService/myPropertyServiceSupplyDemandApi'
import { getListingCoordinates } from '../../../api/myPropertyService/coordinatesApi'
import { PriceMaximizerInput } from '../../../@types/price-maximizer-types'
import { getDefaultLogger } from '../../../services/logger/logger'
import { isAbortedError } from '../../../utils/customFetch'
import { ListingCoordinates } from '../../../@types/my-property-service-types'
import { getRealEstateType } from '../../../api/priceMaximizer/converters/realEstateType'

const isDefaultCoordinates = (coordinates?: ListingCoordinates): boolean => {
  return !coordinates || (coordinates.lat === 0 && coordinates.lng === 0)
}

const stripTransactionSuffix = (realEstateType: string): string => {
  return realEstateType.replace(/_RENT$|_BUY$/g, '')
}

const initializeCoordinates = async (
  realEstateData: PriceMaximizerInput,
  listingId?: string
): Promise<ListingCoordinates | undefined> => {
  if (
    realEstateData.geoCoordinates &&
    realEstateData.geoCoordinates.latitude !== 0 &&
    realEstateData.geoCoordinates.longitude !== 0
  ) {
    return {
      lat: realEstateData.geoCoordinates.latitude,
      lng: realEstateData.geoCoordinates.longitude,
    }
  }

  if (listingId) {
    return await getListingCoordinates(listingId)
  }

  return undefined
}

const fetchRentSupplyDemand = (
  coordinates: ListingCoordinates,
  livingArea: number,
  price: number,
  propertyType: string
): Promise<[number | undefined, number | undefined]> => {
  return Promise.all([
    postSimilarSearchRequestsRentCount({
      location: coordinates,
      livingArea,
      type: propertyType,
    }),
    postSimilarObjectsRentCount({
      location: coordinates,
      livingArea,
      price,
      type: propertyType,
    }),
  ])
}

const fetchSellSupplyDemand = (
  coordinates: ListingCoordinates,
  livingArea: number,
  price: number,
  propertyType: string
): Promise<[number | undefined, number | undefined]> => {
  return Promise.all([
    postSimilarSearchRequestsSellCount({
      location: coordinates,
      livingArea,
      type: propertyType,
    }),
    postSimilarObjectsSellCount({
      location: coordinates,
      livingArea,
      price,
      type: propertyType,
    }),
  ])
}

export const loadPriceMaximizerFromMyPropertyService =
  (realEstateData: PriceMaximizerInput, listingId?: string) => async (dispatch: DispatchType) => {
    try {
      const coordinates = await initializeCoordinates(realEstateData, listingId)

      if (isDefaultCoordinates(coordinates)) {
        getDefaultLogger().info('Invalid or missing coordinates for My Property Service API')
        dispatch(priceMaximizerReset())
        return
      }

      const validCoordinates = coordinates as ListingCoordinates

      // Prepare request data
      const realEstateType = getRealEstateType(realEstateData.category, realEstateData.transaction)
      const propertyTypeOnly = stripTransactionSuffix(realEstateType)

      const livingArea = parseFloat(realEstateData.livingSpace)
      const price = typeof realEstateData.price === 'string' ? parseFloat(realEstateData.price) : realEstateData.price

      const isRent = realEstateData.transaction === 'RENT'

      const [savedSearches, similarObjects] = isRent
        ? await fetchRentSupplyDemand(validCoordinates, livingArea, price, propertyTypeOnly)
        : await fetchSellSupplyDemand(validCoordinates, livingArea, price, propertyTypeOnly)

      if (savedSearches === undefined || similarObjects === undefined) {
        dispatch(priceMaximizerReset())
      } else {
        dispatch(
          priceMaximizerSuccess({
            priceMaximizerResult: {
              savedSearches,
              similarObjects,
            },
          })
        )
      }
    } catch (error) {
      if (isAbortedError(error)) {
        getDefaultLogger().info('loadPriceMaximizerMyPropertyService failed because request is aborted', error)
      } else {
        getDefaultLogger().warn('loadPriceMaximizerMyPropertyService', error)
      }
      dispatch(priceMaximizerReset())
    }
  }
